/**
 * Diff generator
 * Generates diffs between old and new values for edit previews
 */

import type { FieldChange } from '../types/index.js';

/**
 * Generate a diff between old and new values
 */
export function generateDiff(
  oldValue: Record<string, any>,
  newValue: Record<string, any>
): Record<string, FieldChange> {
  const changes: Record<string, FieldChange> = {};

  // Check for modified and added fields
  for (const [key, newVal] of Object.entries(newValue)) {
    const oldVal = oldValue?.[key];

    if (oldVal !== newVal) {
      changes[key] = {
        old: oldVal,
        new: newVal,
        type: oldVal === undefined ? 'added' : 'modified',
      };
    }
  }

  // Check for removed fields
  if (oldValue) {
    for (const [key, oldVal] of Object.entries(oldValue)) {
      if (!(key in newValue)) {
        changes[key] = {
          old: oldVal,
          new: undefined,
          type: 'removed',
        };
      }
    }
  }

  return changes;
}

/**
 * Check if there are any changes
 */
export function hasChanges(changes: Record<string, FieldChange>): boolean {
  return Object.keys(changes).length > 0;
}

/**
 * Get summary of changes
 */
export function getChangeSummary(changes: Record<string, FieldChange>): {
  added: string[];
  modified: string[];
  removed: string[];
} {
  const summary = {
    added: [] as string[],
    modified: [] as string[],
    removed: [] as string[],
  };

  for (const [key, change] of Object.entries(changes)) {
    if (change.type === 'added') {
      summary.added.push(key);
    } else if (change.type === 'modified') {
      summary.modified.push(key);
    } else if (change.type === 'removed') {
      summary.removed.push(key);
    }
  }

  return summary;
}
